// Resilient EventSource for the dashboard's server-sent streams.
//
//   const stop = openStream("/dashboard/oi/stream", (data) => ..., { onStatus });
//   ...
//   stop();   // on unmount
//
// EventSource reconnects on its own only for transient drops; once the browser
// gives up (readyState CLOSED — a 4xx/5xx response, a proxy reset, the backend
// restarting) the object is dead and nothing recovers. This helper re-creates
// the stream with exponential backoff (1 s → 30 s) and, after repeated
// failures, asks /app/verify whether the token is still good: a 401 there runs
// the axios interceptor, which clears the token and sends the app to Login.
//
// A stream can also die WITHOUT an error: the network under it changes (a new
// public IP, Wi-Fi ↔ hotspot), a proxy stalls, and the EventSource stays "open"
// while nothing arrives — the page froze until a manual reload. With `staleMs`
// set, a stream that has delivered neither data nor the server's "ping"
// heartbeat (every 5 s, see SsePublisher) for that long is closed and opened
// again, exactly as a reload would.
import { appVerify, fetchStreamTicket } from "./api";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 30000;
// Failures in a row before we suspect the token rather than the network.
const VERIFY_AFTER_FAILURES = 3;

export function streamUrl(path, ticket = "") {
  // EventSource cannot send headers, so a credential rides the query string:
  // a short-lived stream ticket when we have one (it expires in 120 s, so a
  // leaked URL is worth little), else the app token as before.
  const sep = path.includes("?") ? "&" : "?";
  if (ticket) return `${API_BASE}${path}${sep}ticket=${encodeURIComponent(ticket)}`;
  const token = localStorage.getItem("appToken") || "";
  return `${API_BASE}${path}${sep}x_app_token=${encodeURIComponent(token)}`;
}

// Trade the session token for a stream ticket. Resolves "" when the call fails
// (older backend, network) so the caller falls back to the token.
function getTicket() {
  return fetchStreamTicket()
    .then((r) => (r && r.data && r.data.ticket) || "")
    .catch(() => "");
}

/* `pauseWhenHidden`: close the stream while the tab is hidden and reopen it
 * the moment the tab is visible again (the server pushes its latest snapshot
 * on connect, so nothing is missed). Off by default — a long-running job
 * stream must keep flowing in the background.
 * `staleMs`: restart a stream silent for this long (0 = never). */
export function openStream(path, onMessage, { onStatus, pauseWhenHidden = false, staleMs = 0 } = {}) {
  let source = null;
  let timer = null;
  let watchdog = null;
  let stopped = false;
  let paused = false;
  let failures = 0;
  let attempt = 0;     // a ticket fetched for an older connect() must not open a stream
  let lastSeen = 0;    // last open / message / ping on the current EventSource

  const status = (connected) => { try { onStatus?.(connected); } catch { /* observer only */ } };

  const scheduleReconnect = () => {
    if (stopped || paused || timer) return;
    failures += 1;
    if (failures >= VERIFY_AFTER_FAILURES && failures % VERIFY_AFTER_FAILURES === 0) {
      // A lapsed token: the interceptor logs out on 401. Anything else (backend
      // down) rejects too, and we just keep backing off.
      appVerify().catch(() => {});
    }
    const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_MIN_MS * 2 ** (failures - 1));
    timer = setTimeout(() => { timer = null; connect(); }, delay);
  };

  const connect = () => {
    if (stopped || paused) return;
    if (source) { source.close(); source = null; }
    // A fresh ticket before every EventSource: the browser's own reconnects
    // reuse the URL until the ticket expires (401 → CLOSED), and the next
    // connect() here fetches a new one.
    const mine = ++attempt;
    getTicket().then((ticket) => {
      if (stopped || paused || mine !== attempt) return;
      open(ticket);
    });
  };

  const open = (ticket) => {
    let es;
    try {
      es = new EventSource(streamUrl(path, ticket));
    } catch {
      scheduleReconnect();
      return;
    }
    source = es;
    lastSeen = Date.now();
    es.onopen = () => { failures = 0; lastSeen = Date.now(); status(true); };
    es.onmessage = (event) => {
      failures = 0;
      lastSeen = Date.now();
      let data;
      try { data = JSON.parse(event.data); } catch { return; }
      status(true);
      onMessage(data);
    };
    // Heartbeat: the stream is alive, there is just nothing new yet.
    es.addEventListener("ping", () => { lastSeen = Date.now(); });
    es.onerror = () => {
      status(false);
      // CONNECTING: the browser is retrying by itself — leave it alone.
      // CLOSED: it gave up; only a new EventSource can bring the stream back.
      if (es.readyState === EventSource.CLOSED) {
        if (source === es) source = null;
        es.close();
        scheduleReconnect();
      }
    };
  };

  // Also covers a stream stuck in CONNECTING (a request that never gets an
  // answer): lastSeen starts when the EventSource is created.
  const checkStale = () => {
    if (stopped || paused || !source || timer) return;
    if (Date.now() - lastSeen <= staleMs) return;
    source.close();
    source = null;
    status(false);
    scheduleReconnect();
  };
  if (staleMs > 0) watchdog = setInterval(checkStale, Math.max(250, Math.min(5000, staleMs / 4)));

  const onVisibility = () => {
    if (stopped) return;
    if (document.visibilityState === "hidden") {
      paused = true;
      clearTimeout(timer);
      timer = null;
      if (source) { source.close(); source = null; }
      status(false);
    } else if (paused) {
      paused = false;
      failures = 0;
      connect();
    }
  };
  if (pauseWhenHidden && typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState === "hidden") paused = true;
  }

  connect();

  return function stop() {
    stopped = true;
    clearTimeout(timer);
    timer = null;
    clearInterval(watchdog);
    watchdog = null;
    if (source) { source.close(); source = null; }
    if (pauseWhenHidden && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibility);
    }
  };
}
